import { ArrowLeft, Check, ExternalLink, Minus, X } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { Link, useParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'
import { CoiGeneratorUser, useCoiGeneratorDetail, useMarkCorrect } from '../../../../models/coiGenerator'
import { AgentRunCard } from './AgentRunCard'

function formatUserName(user: CoiGeneratorUser | null): string {
  if (!user) return '\u2014'
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
  return name || user.email
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function CoiGeneratorDetail() {
  const { id } = useParams()
  const { item, isLoading } = useCoiGeneratorDetail(id)
  const markCorrect = useMarkCorrect()

  if (isLoading || !item) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <SkeletonRows rows={4} />
          <SkeletonRows rows={4} />
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>COI Generator Detail</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <div>
          <Button variant="link" className="px-0" asChild>
            <Link to="/insure/internal/coi-generator">
              <ArrowLeft className="mr-1 size-4" />
              Back to COI Generator
            </Link>
          </Button>
        </div>

        <div className="mb-2 text-2xl font-semibold">COI Generator Detail</div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Documents */}
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div>
                <div className="text-muted-foreground mb-1 text-sm font-medium">
                  {(item.policyFiles ?? []).length > 1 ? 'Policy Documents' : 'Policy Document'}
                </div>
                {(item.policyFiles ?? []).length > 0 ? (
                  <div className="flex flex-col gap-1">
                    {(item.policyFiles ?? []).map((file) => (
                      <a
                        key={file.url}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        {file.name}
                        <ExternalLink className="size-3" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">No policy uploaded</span>
                )}
              </div>
              <div>
                <div className="text-muted-foreground mb-1 text-sm font-medium">COI Document</div>
                {item.coiFileUrl ? (
                  <a
                    href={item.coiFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    {item.coiFileName}
                    <ExternalLink className="size-3" />
                  </a>
                ) : (
                  <span className="text-muted-foreground">No COI generated yet</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div>
                <div className="text-muted-foreground mb-1 text-sm font-medium">Created</div>
                <div>{formatDate(item.dateCreated)}</div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1 text-sm font-medium">Created by</div>
                <div>{formatUserName(item.createdBy)}</div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1 text-sm font-medium">Reviewed by</div>
                <div>{formatUserName(item.reviewedBy)}</div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1 text-sm font-medium">COI Generated</div>
                <div className="flex items-center gap-1">
                  {item.hasCoi ? (
                    <>
                      <Check className="size-4 text-green-600" /> Yes
                    </>
                  ) : (
                    <>
                      <X className="size-4 text-neutral-400" /> No
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1 text-sm font-medium">Correct</div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1">
                    {item.isCorrect === true ? (
                      <>
                        <Check className="size-4 text-green-600" /> Yes
                      </>
                    ) : item.isCorrect === false ? (
                      <>
                        <X className="size-4 text-red-500" /> No
                      </>
                    ) : (
                      <>
                        <Minus className="size-4 text-neutral-400" /> Not reviewed
                      </>
                    )}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => markCorrect.mutate({ id: item.id, isCorrect: true })}
                    disabled={markCorrect.isPending || item.isCorrect === true}
                  >
                    Mark Correct
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => markCorrect.mutate({ id: item.id, isCorrect: false })}
                    disabled={markCorrect.isPending || item.isCorrect === false}
                  >
                    Mark Incorrect
                  </Button>
                </div>
                {item.isCorrect === false && (
                  <p className="text-muted-foreground mt-2 text-sm">
                    Please create a{' '}
                    <a
                      href={`https://linear.app/cloudtrucks/team/CAI/new?label=coi%20generation&title=Incorrect%20COI%20Generation&description=${encodeURIComponent(window.location.href)}.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      linear ticket
                    </a>{' '}
                    with details what is incorrect about thid COI document.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle>Parsed Policy</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(item.metadata).length === 0 ? (
              <div className="text-muted-foreground">No metadata available</div>
            ) : (
              <pre className="overflow-auto rounded-md bg-neutral-50 p-4 text-sm">
                {JSON.stringify(item.metadata, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>

        <AgentRunCard agentRun={item.agentRun} />
      </div>
    </>
  )
}
